/**
 * useEventAttendees — every attendee of one event, from ListEventAttendees, page by page.
 *
 * The first page also carries every nomination for the event; those lead the list, and each page
 * of campaign members is merged in behind them (utils/attendees `mergeAttendees`).
 *
 * With SUB_CAMPAIGN_ATTENDEES_ENABLED (config.ts) the request also lists the campaigns beneath the
 * event, one row per membership, and the rows are merged into people as they arrive
 * (utils/attendeeMerger). The flag-off listing takes the same path; nothing merges there. The walk
 * itself — empty pages that still carry a token, a 400 that means the hierarchy changed and
 * restarts it — is `loadMergedAttendees`.
 *
 * The event detail page's Attendance List and Details by BU both read this one fetch, so the card
 * that lists the attendees and the card that counts their nominations cannot disagree.
 * ListNominations stays with the Nomination List page.
 *
 * The same shape as useNominationList: aborts on unmount, ignores a late response, keeps "empty"
 * and "failed" apart. `loading` stays true until the last page, while `attendees` grows with each.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  eventHubCalendarClient,
  type EventAttendee,
  type Nomination,
} from '../client/EventCalendarClient';
import { SUB_CAMPAIGN_ATTENDEES_ENABLED } from '../../config';
import { loadMergedAttendees } from '../utils/attendeeMerger';
import { mergeAttendees, withNomination } from '../utils/attendees';

export interface EventAttendeesState {
  attendees: readonly EventAttendee[];
  loading: boolean;
  /** Error message, or null. */
  error: string | null;
  /** Swaps in a nomination the service just updated (an attendance confirmation), no re-fetch. */
  replaceNomination: (updated: Nomination) => void;
}

export function useEventAttendees(
  eventId: string | undefined,
  includeSubCampaigns: boolean = SUB_CAMPAIGN_ATTENDEES_ENABLED,
): EventAttendeesState {
  const [attendees, setAttendees] = useState<readonly EventAttendee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* Nominations updated on this page since the fetch began. Each page rebuilds the rows from what
     the service sent, so without these a confirmation ticked while later pages were still loading
     would be undone by the next one. */
  const updatedRef = useRef(new Map<string, Nomination>());

  const replaceNomination = useCallback((updated: Nomination) => {
    if (updated.nominationId) updatedRef.current.set(updated.nominationId, updated);
    setAttendees((list) => withNomination(list, updated));
  }, []);

  useEffect(() => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    const client = eventHubCalendarClient();

    setAttendees([]);
    setLoading(true);
    setError(null);
    updatedRef.current = new Map();

    loadMergedAttendees(
      (nextToken) =>
        client.listEventAttendees({
          eventId,
          nextToken,
          signal: controller.signal,
          // The same value on every page, restarts included: a token is bound to it.
          includeSubCampaigns,
        }),
      ({ people, nominations }) => {
        if (cancelled) return;
        let rows = mergeAttendees(nominations, people);
        for (const u of updatedRef.current.values()) rows = withNomination(rows, u);
        setAttendees(rows);
      },
      controller.signal,
    )
      .then(() => {
        if (!cancelled) setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [eventId, includeSubCampaigns]);

  return { attendees, loading, error, replaceNomination };
}
